import { useState, useEffect, lazy, Suspense } from 'react';
import SEOHead from '../components/common/SEOHead';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import emailjs from '@emailjs/browser';
import YouTubeSection from '../components/youtube/YouTubeSection';
import YouTubeShortsSection from '../components/youtube/YouTubeShortsSection';
import YouTubeCommentsSection from '../components/youtube/YouTubeCommentsSection';
import { API_ENDPOINTS } from '../config/constants';
import { CookieBanner } from '../components/ui/CookieBanner';
import { EMAILJS_CONFIG } from '../config/emailjs';

const GLSLHills = lazy(() => import('../components/three/GLSLHills'));

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string;
  featured_image_alt: string;
  published_at: string;
  reading_time: number;
  view_count: number;
  location_name: string;
  location_country: string;
  category_name: string;
  category_color: string;
  first_name: string;
  last_name: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  color: string;
  post_count?: number;
}

interface GalleryImage {
  id: string;
  url: string;
  title?: string;
  location?: string;
}

interface NewsArticle {
  title: string;
  description: string;
  url: string;
  source: string;
  image: string | null;
  publishedAt: string;
}

const HomePage = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');

  const { scrollY } = useScroll();
  const heroContentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  useEffect(() => {
    fetchRecentPosts();
    fetchCategories();
    fetchGallery();
    fetchNews();
  }, []);

  const fetchRecentPosts = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.posts}?limit=6`);
      const data = await response.json();
      if (data.success) {
        setPosts(data.data);
      } else {
        setError('Failed to load posts');
      }
    } catch {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.categories);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.slice(0, 8));
      }
    } catch {
      console.error('Failed to fetch categories');
    }
  };

  const fetchNews = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.news);
      const json = await res.json();
      if (json.success && json.data?.length) {
        setNewsArticles(json.data.slice(0, 6));
      }
    } catch {
      /* news is non-critical */
    }
  };

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.gallery}?limit=8`);
      const json = await res.json();
      if (json.success) {
        setGalleryImages(
          json.data.map((it: any) => ({
            id: it.id,
            url: (it.cloudinary_url || '').replace(/\/v\d+\//, '/'),
            title: it.title || undefined,
            location: it.location || undefined,
          }))
        );
      }
    } catch {
      /* gallery is non-critical */
    }
  };

  const editorialPosts = posts.slice(1, 4);
  const morePosts = posts.slice(4);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setNewsletterStatus('sending');
    try {
      const now = new Date();
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          name: 'Newsletter Subscriber',
          initial: 'N',
          time: now.toLocaleString(),
          message: `New newsletter subscription!\n\nEmail: ${newsletterEmail}`,
          replyLink: `mailto:${newsletterEmail}?subject=Welcome to our Newsletter!`,
          year: now.getFullYear().toString(),
        },
        EMAILJS_CONFIG.publicKey
      );
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setNewsletterStatus('error');
    }
  };

  /* ─── Loading Skeleton ─── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1426]">
        <div className="min-h-screen flex items-end">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 pt-40 w-full">
            <div className="animate-pulse space-y-6">
              <div className="h-4 bg-white/10 rounded w-28" />
              <div className="h-16 bg-white/10 rounded w-2/3 max-w-lg" />
              <div className="h-10 bg-white/[0.06] rounded w-1/2 max-w-sm" />
              <div className="h-4 bg-white/[0.06] rounded w-80" />
              <div className="h-4 bg-white/[0.06] rounded w-64" />
              <div className="flex gap-4 pt-6">
                <div className="h-14 bg-white/10 rounded-full w-44" />
                <div className="h-14 bg-white/[0.06] rounded-full w-36" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Mermaids Of The Moment"
        description="Mermaids Of The Moment is a travel blog sharing real adventures, hidden gems, honest travel tips, and stunning photography from around the world."
        path="/"
      />

      <div className="bg-[#faf6f1]">
        {/* ════════════════════════════════════════════
            HERO — Full-bleed cinematic with parallax
        ════════════════════════════════════════════ */}
        <section className="relative min-h-screen flex flex-col overflow-hidden bg-[#0b1426]">
          {/* 3D animated hills background */}
          <Suspense fallback={null}>
            <GLSLHills
              className="pointer-events-none opacity-60"
              cameraZ={125}
              speed={0.3}
            />
          </Suspense>

          {/* Main content — 2-col grid on desktop: text left, cards right */}
          <motion.div
            className="relative z-10 w-full flex-1 flex items-center"
            style={{ opacity: heroContentOpacity }}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-0 w-full lg:grid lg:grid-cols-12 lg:gap-8 lg:items-center">
              {/* LEFT — hero text */}
              <div className="lg:col-span-7 xl:col-span-6">
                {/* Italic tagline */}
                <motion.p
                  className="font-editorial italic text-[#d4634a] text-lg md:text-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3 }}
                >
                  Travel Journal
                </motion.p>

                {/* Main title */}
                <motion.h1
                  className="font-editorial font-bold text-white mt-4 leading-[0.88] tracking-[-0.02em]"
                  style={{ fontSize: 'clamp(2.2rem, 8vw, 7rem)' }}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  Mermaids
                  <br />
                  <span className="font-light italic text-white/50">
                    of the
                  </span>
                  <br />
                  Moment
                </motion.h1>

                {/* Tagline */}
                <motion.p
                  className="text-white/45 mt-5 max-w-md text-base md:text-lg leading-relaxed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.8 }}
                >
                  Stories from the edge of the world. Travel, adventure, and the
                  fleeting beauty of every moment.
                </motion.p>

                {/* CTA buttons */}
                <motion.div
                  className="flex flex-wrap gap-4 mt-7"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <Link
                    to="/blog"
                    className="inline-flex items-center px-8 py-4 bg-[#d4634a] text-white text-sm font-medium rounded-full hover:bg-[#c25438] transition-all duration-300 hover:shadow-lg hover:shadow-[#d4634a]/20"
                  >
                    Explore Stories
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </Link>
                  <a
                    href="https://www.youtube.com/@mermaidsofthemoment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-4 border border-white/20 text-white text-sm font-medium rounded-full hover:bg-white/10 transition-all duration-300"
                  >
                    Watch Films
                  </a>
                </motion.div>
              </div>

              {/* RIGHT — 3 compact cards, desktop only */}
              {posts.length > 0 && (
                <div className="hidden lg:flex lg:col-span-5 xl:col-span-6 flex-col gap-4">
                  {posts.slice(0, 3).map((post, i) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: 0.9 + i * 0.15,
                        ease: 'easeOut',
                      }}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className="group flex gap-4 items-center rounded-2xl backdrop-blur-md bg-white/[0.06] border border-white/[0.07] p-3 pr-5 transition-all duration-500 hover:bg-white/[0.1] hover:border-white/[0.14] hover:shadow-lg hover:shadow-[#d4634a]/[0.04] hover:scale-[1.02]"
                      >
                        {/* Thumbnail */}
                        <div className="relative w-36 h-28 flex-shrink-0 overflow-hidden rounded-xl">
                          <img
                            src={post.featured_image || '/placeholder.jpg'}
                            alt={post.featured_image_alt || post.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 py-1">
                          {post.category_name && (
                            <span className="inline-block px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-white bg-[#d4634a]/90 rounded-full mb-2">
                              {post.category_name}
                            </span>
                          )}
                          <h3 className="font-editorial text-base text-white font-semibold leading-snug line-clamp-2 group-hover:text-[#d4634a] transition-colors">
                            {post.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-2 text-[10px] text-white/30">
                            {post.location_name && (
                              <>
                                <span>{post.location_name}</span>
                                <span className="w-0.5 h-0.5 bg-white/20 rounded-full" />
                              </>
                            )}
                            <span>
                              {format(
                                new Date(post.published_at),
                                'MMM dd, yyyy'
                              )}
                            </span>
                            <span className="w-0.5 h-0.5 bg-white/20 rounded-full" />
                            <span>{post.reading_time} min read</span>
                          </div>
                        </div>

                        <ArrowRightIcon className="w-4 h-4 text-white/10 group-hover:text-[#d4634a]/60 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Horizontal post cards — mobile/tablet only (staggered cards show on lg+) */}
          {posts.length > 1 && (
            <motion.div
              className="relative z-20 w-full pb-14 lg:hidden"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {posts.slice(0, 3).map((post, i) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 1.3 + i * 0.12 }}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className="group flex gap-4 items-center rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/15 transition-all duration-500 p-2.5 pr-5"
                      >
                        <div className="relative overflow-hidden rounded-lg flex-shrink-0 w-20 h-20">
                          <img
                            src={post.featured_image || '/placeholder.jpg'}
                            alt={post.featured_image_alt || post.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          {post.category_name && (
                            <span className="text-[#d4634a] text-[9px] font-bold uppercase tracking-[0.15em]">
                              {post.category_name}
                            </span>
                          )}
                          <h4 className="text-white text-sm font-semibold leading-snug line-clamp-2 group-hover:text-[#d4634a] transition-colors mt-0.5">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/25">
                            <span>
                              {format(new Date(post.published_at), 'MMM dd')}
                            </span>
                            <span className="w-0.5 h-0.5 bg-white/20 rounded-full" />
                            <span>{post.reading_time} min</span>
                          </div>
                        </div>
                        <ArrowRightIcon className="w-3.5 h-3.5 text-white/15 group-hover:text-[#d4634a] group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6 }}
          >
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{
                repeat: Infinity,
                duration: 1.8,
                ease: 'easeInOut',
              }}
              className="flex flex-col items-center"
            >
              <span className="text-white/25 text-[9px] uppercase tracking-[0.35em] mb-2">
                Scroll
              </span>
              <div className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent" />
            </motion.div>
          </motion.div>
        </section>

        {/* ════════════════════════════════════════════
            CATEGORY MARQUEE — Infinite scroll ticker
        ════════════════════════════════════════════ */}
        {categories.length > 0 && (
          <section className="py-5 bg-[#faf6f1] border-y border-stone-200/50 overflow-hidden">
            <div className="relative">
              <div className="flex animate-marquee whitespace-nowrap">
                {[...categories, ...categories, ...categories].map((cat, i) => (
                  <Link
                    key={`${cat.id}-${i}`}
                    to={`/blog?category=${cat.slug}`}
                    className="inline-flex items-center mx-4 sm:mx-6 lg:mx-8 text-[11px] font-medium text-stone-400 hover:text-[#d4634a] transition-colors uppercase tracking-[0.2em]"
                  >
                    <span className="w-1 h-1 bg-[#d4634a]/40 rounded-full mr-3 flex-shrink-0" />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════
            TRENDING NEWS — Card grid
        ════════════════════════════════════════════ */}
        {newsArticles.length > 0 && (
          <section className="py-12 md:py-20 lg:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              {/* Section header */}
              <div className="flex items-end justify-between mb-8 md:mb-14">
                <div>
                  <motion.span
                    className="text-[#d4634a] text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.25em]"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                  >
                    Trending Now
                  </motion.span>
                  <motion.h2
                    className="font-editorial text-3xl md:text-5xl lg:text-6xl text-stone-900 mt-2 md:mt-3 tracking-tight font-bold"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                  >
                    World{' '}
                    <span className="italic font-light text-stone-300">
                      headlines
                    </span>
                  </motion.h2>
                </div>
              </div>

              {/* News cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
                {newsArticles.map((article, i) => (
                  <motion.a
                    key={`${article.url}-${i}`}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-[#faf6f1] rounded-xl md:rounded-2xl overflow-hidden border border-stone-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{
                      delay: 0.12 * i,
                      duration: 0.6,
                    }}
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden aspect-video bg-stone-100">
                      {article.image ? (
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0b1426] to-[#1a2a4a] flex items-center justify-center">
                          <span className="font-editorial italic text-3xl text-white/20">
                            {article.source}
                          </span>
                        </div>
                      )}
                      {/* Source badge */}
                      <span className="absolute top-3 left-3 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white bg-stone-900/70 backdrop-blur-sm rounded-full">
                        {article.source}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-5 lg:p-6">
                      <h3 className="text-lg font-bold text-stone-900 leading-snug line-clamp-2 group-hover:text-[#d4634a] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-stone-600 text-sm mt-2.5 line-clamp-2">
                        {article.description}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-200/60">
                        <span className="text-[11px] text-stone-500">
                          {article.publishedAt
                            ? format(
                                new Date(article.publishedAt),
                                'MMM dd, yyyy'
                              )
                            : ''}
                        </span>
                        <span className="inline-flex items-center text-[11px] font-semibold text-[#d4634a] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                          Read
                          <ArrowRightIcon className="ml-1 w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════
            EDITORIAL GRID — Magazine-style articles
        ════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28 bg-[#faf6f1]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Section header */}
            <div className="flex items-end justify-between mb-14">
              <div>
                <motion.span
                  className="text-[#d4634a] text-[11px] font-semibold uppercase tracking-[0.25em]"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  Latest Stories
                </motion.span>
                <motion.h2
                  className="font-editorial text-4xl md:text-5xl lg:text-6xl text-stone-900 mt-3 tracking-tight font-bold"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                >
                  Fresh{' '}
                  <span className="italic font-light text-stone-300">
                    from the road
                  </span>
                </motion.h2>
              </div>
              <Link
                to="/blog"
                className="hidden md:inline-flex items-center text-stone-400 hover:text-[#d4634a] transition-colors text-[11px] font-semibold uppercase tracking-[0.2em]"
              >
                View all
                <ArrowRightIcon className="ml-2 w-3.5 h-3.5" />
              </Link>
            </div>

            {error ? (
              <div className="text-center py-16">
                <p className="text-stone-500 mb-4">{error}</p>
                <button
                  onClick={fetchRecentPosts}
                  className="px-8 py-3 bg-[#d4634a] text-white rounded-full hover:bg-[#c25438] transition-colors text-sm font-medium"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                {/* Main grid: large feature + 2 stacked */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                  {/* Large feature card */}
                  {editorialPosts[0] && (
                    <motion.div
                      className="lg:col-span-7"
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-80px' }}
                      transition={{ duration: 0.7 }}
                    >
                      <Link
                        to={`/blog/${editorialPosts[0].slug}`}
                        className="group block"
                      >
                        <div className="relative overflow-hidden rounded-2xl">
                          <img
                            src={
                              editorialPosts[0].featured_image ||
                              '/placeholder.jpg'
                            }
                            alt={
                              editorialPosts[0].featured_image_alt ||
                              editorialPosts[0].title
                            }
                            className="w-full h-[280px] sm:h-[340px] md:h-[420px] lg:h-[540px] object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                          {/* Editorial number watermark */}
                          <div className="absolute top-6 left-7">
                            <span className="font-editorial italic text-6xl lg:text-7xl text-white/[0.12] leading-none select-none">
                              01
                            </span>
                          </div>

                          {/* Content overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-9">
                            {editorialPosts[0].category_name && (
                              <span className="inline-block px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-white bg-[#d4634a] rounded-full mb-4">
                                {editorialPosts[0].category_name}
                              </span>
                            )}
                            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-snug tracking-tight">
                              {editorialPosts[0].title}
                            </h3>
                            <p className="text-white/50 mt-3 line-clamp-2 max-w-lg text-sm md:text-base">
                              {editorialPosts[0].excerpt}
                            </p>
                            <div className="flex items-center gap-3 mt-5 text-[11px] text-white/35">
                              <span>
                                {format(
                                  new Date(editorialPosts[0].published_at),
                                  'MMMM dd, yyyy'
                                )}
                              </span>
                              <span className="w-1 h-1 bg-white/20 rounded-full" />
                              <span>
                                {editorialPosts[0].reading_time} min read
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  )}

                  {/* Stacked side cards */}
                  <div className="lg:col-span-5 flex flex-col gap-8 lg:gap-10">
                    {editorialPosts.slice(1, 3).map((post, i) => (
                      <motion.div
                        key={post.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{
                          delay: 0.15 * (i + 1),
                          duration: 0.6,
                        }}
                      >
                        <Link
                          to={`/blog/${post.slug}`}
                          className="group flex gap-5"
                        >
                          {/* Number */}
                          <span className="font-editorial italic text-5xl text-stone-200/80 flex-shrink-0 w-10 leading-none hidden sm:block select-none">
                            {String(i + 2).padStart(2, '0')}
                          </span>

                          <div className="flex-1">
                            <div className="relative overflow-hidden rounded-xl mb-4">
                              <img
                                src={post.featured_image || '/placeholder.jpg'}
                                alt={post.featured_image_alt || post.title}
                                className="w-full h-40 sm:h-48 md:h-52 object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              {post.category_name && (
                                <span className="absolute top-3 left-3 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white bg-stone-900/60 backdrop-blur-sm rounded-full">
                                  {post.category_name}
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-bold text-stone-900 group-hover:text-[#d4634a] transition-colors leading-snug line-clamp-2">
                              {post.title}
                            </h3>
                            <p className="text-stone-400 text-sm mt-2 line-clamp-2">
                              {post.excerpt}
                            </p>
                            <div className="flex items-center gap-3 mt-3 text-[11px] text-stone-300">
                              <span>
                                {format(
                                  new Date(post.published_at),
                                  'MMM dd, yyyy'
                                )}
                              </span>
                              <span className="w-1 h-1 bg-stone-200 rounded-full" />
                              <span>{post.reading_time} min read</span>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* More stories — compact list */}
                {morePosts.length > 0 && (
                  <div className="mt-16 pt-16 border-t border-stone-200/60">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
                      {morePosts.map((post, i) => (
                        <motion.div
                          key={post.id}
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            delay: 0.08 * i,
                            duration: 0.5,
                          }}
                        >
                          <Link
                            to={`/blog/${post.slug}`}
                            className="group flex items-center gap-5 py-5 border-b border-stone-100"
                          >
                            <img
                              src={post.featured_image || '/placeholder.jpg'}
                              alt={post.featured_image_alt || post.title}
                              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                              loading="lazy"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-stone-800 group-hover:text-[#d4634a] transition-colors line-clamp-1 text-sm">
                                {post.title}
                              </h4>
                              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-stone-300">
                                {post.location_name && (
                                  <span>{post.location_name}</span>
                                )}
                                <span>{post.reading_time} min read</span>
                              </div>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 text-stone-200 group-hover:text-[#d4634a] group-hover:translate-x-1 transition-all flex-shrink-0" />
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobile "View all" */}
                <div className="mt-12 text-center md:hidden">
                  <Link
                    to="/blog"
                    className="inline-flex items-center px-8 py-4 bg-stone-900 text-white text-sm font-medium rounded-full"
                  >
                    View All Stories
                    <ArrowRightIcon className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>

        {/* ════════════════════════════════════════════
            GALLERY STRIP — Horizontal photo scroll
        ════════════════════════════════════════════ */}
        {galleryImages.length > 0 && (
          <section className="py-20 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
              <div className="flex items-end justify-between">
                <div>
                  <motion.span
                    className="text-[#d4634a] text-[11px] font-semibold uppercase tracking-[0.25em]"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                  >
                    Gallery
                  </motion.span>
                  <motion.h2
                    className="font-editorial text-4xl md:text-5xl lg:text-6xl text-stone-900 mt-3 tracking-tight font-bold"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                  >
                    Captured{' '}
                    <span className="italic font-light text-stone-300">
                      moments
                    </span>
                  </motion.h2>
                </div>
                <Link
                  to="/gallery"
                  className="hidden md:inline-flex items-center text-stone-400 hover:text-[#d4634a] transition-colors text-[11px] font-semibold uppercase tracking-[0.2em]"
                >
                  View Gallery
                  <ArrowRightIcon className="ml-2 w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            <div className="overflow-x-auto scrollbar-hide">
              <motion.div
                className="flex gap-5 px-6 lg:px-8 pb-4"
                initial={{ opacity: 0, x: 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                {galleryImages.map(image => (
                  <Link
                    key={image.id}
                    to="/gallery"
                    className="group flex-shrink-0 w-60 sm:w-64 md:w-72 lg:w-80"
                  >
                    <div className="relative overflow-hidden rounded-xl">
                      <img
                        src={image.url}
                        alt={image.title || 'Travel photo'}
                        className="w-full h-96 object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                        <p className="text-white font-medium text-sm">
                          {image.title || 'Adventure'}
                        </p>
                        {image.location && (
                          <p className="text-white/50 text-xs mt-1">
                            {image.location}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </motion.div>
            </div>

            {/* Mobile gallery link */}
            <div className="mt-8 text-center md:hidden px-6">
              <Link
                to="/gallery"
                className="inline-flex items-center text-[#d4634a] text-sm font-medium"
              >
                View Full Gallery
                <ArrowRightIcon className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </section>
        )}

        <YouTubeCommentsSection />

        {/* ════════════════════════════════════════════
            NEWSLETTER — Dark atmospheric section
        ════════════════════════════════════════════ */}
        <section className="relative py-24 lg:py-32 bg-[#0b1426] overflow-hidden">
          {/* Grain texture */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            }}
          />
          {/* Decorative rings */}
          <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/[0.02] pointer-events-none" />
          <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] rounded-full border border-white/[0.04] pointer-events-none" />

          <div className="relative z-10 max-w-2xl mx-auto px-6 lg:px-8 text-center">
            <motion.span
              className="text-[#d4634a] text-[11px] font-semibold uppercase tracking-[0.25em]"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Newsletter
            </motion.span>

            <motion.h2
              className="font-editorial text-4xl md:text-5xl lg:text-6xl text-white mt-5 tracking-tight font-bold"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Stay in{' '}
              <span className="italic font-light text-white/40">
                the current
              </span>
            </motion.h2>

            <motion.p
              className="text-white/30 mt-5 text-base md:text-lg"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Stories delivered to your inbox. No noise, just the good stuff.
            </motion.p>

            <motion.div
              className="mt-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {newsletterStatus === 'success' ? (
                <p className="text-[#d4634a] font-editorial italic text-xl">
                  Welcome aboard. We'll keep you in the loop.
                </p>
              ) : (
                <form
                  onSubmit={handleNewsletterSubmit}
                  className="flex flex-col sm:flex-row gap-3 max-w-sm sm:max-w-md mx-auto"
                >
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={newsletterEmail}
                    onChange={e => setNewsletterEmail(e.target.value)}
                    required
                    className="flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-white/[0.05] border border-white/10 rounded-full text-white placeholder-white/20 focus:outline-none focus:border-[#d4634a]/50 focus:bg-white/[0.08] transition-all text-sm"
                  />
                  <button
                    type="submit"
                    disabled={newsletterStatus === 'sending'}
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-[#d4634a] text-white text-sm font-medium rounded-full hover:bg-[#c25438] transition-all duration-300 disabled:opacity-50"
                  >
                    {newsletterStatus === 'sending'
                      ? 'Sending...'
                      : 'Subscribe'}
                  </button>
                </form>
              )}
              {newsletterStatus === 'error' && (
                <p className="mt-4 text-red-400 text-sm">
                  Something went wrong. Please try again.
                </p>
              )}
            </motion.div>
          </div>
        </section>

        {/* ════════════════════════════════════════════
            VIDEO SECTION — Cinematic header + embeds
        ════════════════════════════════════════════ */}
        <section className="py-20 bg-stone-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center mb-2">
            <motion.span
              className="text-[#d4634a] text-[11px] font-semibold uppercase tracking-[0.25em]"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Video
            </motion.span>
            <motion.h2
              className="font-editorial text-4xl md:text-5xl lg:text-6xl text-white mt-3 tracking-tight font-bold"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Watch{' '}
              <span className="italic font-light text-white/40">
                our adventures
              </span>
            </motion.h2>
          </div>
        </section>

        <YouTubeSection />
        <YouTubeShortsSection />

        {/* Cookie Consent */}
        <CookieBanner position="bottom" />
      </div>
    </>
  );
};

export default HomePage;
